import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { T, F, th as palette, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import BricksLoader from "@/components/BricksLoader";
import {
  FILTERS, SEGMENTS, localToday, matchesFilter, matchesSearch, segmentKey, segmentLabel,
  sortRows, statusLabel, nextStepParts, contactLine, emptyMessage, shortDay, channelLabel,
} from "@/components/outreach/outreachBoardModel";

// Outreach board: the cold-outreach campaign, managed natively in the app.
// Backed by backend/outreach.py (owner-gated): prospects carry a 5-touch
// sequence with approvals and due dates, a replies/feedback log, and a
// CASL suppression list. Any reply halts that prospect's sequence.

function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const inputStyle = (p) => ({
  width: '100%', padding: '9px 12px', borderRadius: 2,
  border: `1px solid ${p.hair}`, background: p.surf, color: p.ink,
  fontFamily: F.display, fontSize: 13, outline: 'none',
});

const CSS = `
.ob-row { display: grid; grid-template-columns: minmax(0,1fr) auto; column-gap: 16px; row-gap: 4px; align-items: start; }
.ob-row .ob-next { grid-column: 1 / -1; }
.ob-row:hover { background: var(--ob-hover); }
.ob-row:focus-visible { outline: 2px solid ${T.brick}; outline-offset: -2px; }
@media (min-width: 768px) {
  .ob-row { grid-template-columns: minmax(0,1.6fr) minmax(0,1fr) 96px; align-items: center; }
  .ob-row .ob-next { grid-column: auto; }
  .ob-row .ob-status { order: 3; text-align: right; }
}
.ob-seg::-webkit-scrollbar { display: none; }
`;

function Figure({ children, p }) {
  return <span style={{ fontFamily: F.mono, fontSize: 11.5, letterSpacing: 0, color: p.ink2 }}>{children}</span>;
}

function Label({ children, p }) {
  return <div style={{ fontSize: 12, fontWeight: 500, color: p.ink3, marginBottom: 10 }}>{children}</div>;
}

function Meta({ items, p }) {
  const list = items.filter(Boolean);
  if (!list.length) return null;
  return (
    <div style={{ fontSize: 12.5, color: p.ink3, display: 'flex', flexWrap: 'wrap', gap: '4px 0' }}>
      {list.map((it, i) => (
        <span key={i} style={{ color: it.tone || p.ink3 }}>
          {i > 0 && <span style={{ margin: '0 8px', color: p.ink4 }}>·</span>}
          {it.figure ? <span style={{ fontFamily: F.mono, fontSize: 11.5 }}>{it.text}</span> : it.text}
        </span>
      ))}
    </div>
  );
}

export default function OutreachPage() {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, replied: 0, halted: 0, due_now: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [segment, setSegment] = useState('all');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyKind, setReplyKind] = useState('reply');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get('/outreach/board')
      .then(r => { setRows(r.data?.prospects || []); setStats(r.data?.stats || stats); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openDetail = (id) => {
    setDetailLoading(true);
    setDetail({ prospect: rows.find(r => r.id === id) || {}, touches: [], replies: [] });
    api.get(`/outreach/prospects/${id}`)
      .then(r => { setDetail(r.data); setDetailLoading(false); })
      .catch(() => { toast.error('Couldn’t load prospect'); setDetailLoading(false); });
  };

  const refreshDetail = (id) => {
    api.get(`/outreach/prospects/${id}`).then(r => setDetail(r.data)).catch(() => {});
    load();
  };

  const markSent = async (touchNo) => {
    if (!detail?.prospect?.id) return;
    setBusy(true);
    try {
      await api.post(`/outreach/prospects/${detail.prospect.id}/touches/${touchNo}/sent`, {});
      toast.success(`Touch ${touchNo} marked sent`);
      refreshDetail(detail.prospect.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark sent');
    } finally { setBusy(false); }
  };

  const logReply = async () => {
    if (!detail?.prospect?.id || !replyBody.trim()) return toast.error('Reply text is required');
    setBusy(true);
    try {
      await api.post(`/outreach/prospects/${detail.prospect.id}/replies`, { body: replyBody.trim(), kind: replyKind });
      toast.success('Logged. Sequence halted.');
      setReplyBody('');
      refreshDetail(detail.prospect.id);
    } catch { toast.error('Failed to log reply'); }
    finally { setBusy(false); }
  };

  const toggleHalt = async () => {
    if (!detail?.prospect?.id) return;
    const halted = !detail.prospect.sequence_halted;
    setBusy(true);
    try {
      await api.post(`/outreach/prospects/${detail.prospect.id}/halt`, { halted, reason: halted ? 'manual halt' : null });
      toast.success(halted ? 'Sequence halted' : 'Sequence resumed');
      refreshDetail(detail.prospect.id);
    } catch { toast.error('Failed to update sequence'); }
    finally { setBusy(false); }
  };

  const suppress = async () => {
    const pro = detail?.prospect;
    if (!pro) return;
    const destination = pro.channel === 'email' ? pro.email : pro.instagram;
    if (!destination) return toast.error('No destination on file to suppress');
    setBusy(true);
    try {
      await api.post('/outreach/suppressions', { destination, channel: pro.channel, reason: 'manual suppress', source: 'board', prospect_id: pro.id });
      toast.success('Suppressed. They won’t be contacted again.');
      refreshDetail(pro.id);
    } catch { toast.error('Failed to suppress'); }
    finally { setBusy(false); }
  };

  const today = localToday();
  const visible = useMemo(() => sortRows(
    rows.filter(r => matchesFilter(r, filter, today)
      && (segment === 'all' || segmentKey(r) === segment)
      && matchesSearch(r, search)),
    today,
  ), [rows, filter, segment, search, today]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
      <BricksLoader variant="assemble" size={40} color={T.brick} />
    </div>
  );

  const pro = detail?.prospect;
  const dueCount = rows.filter(r => matchesFilter(r, 'due', today)).length;
  const summary = [
    { label: 'Due today', value: stats.due_now ?? dueCount },
    { label: 'Active', value: stats.active ?? 0 },
    { label: 'Replied', value: stats.replied ?? 0 },
  ];

  return (
    <div data-testid="outreach-page" style={{ display: 'flex', flexDirection: 'column', gap: 28, fontFamily: F.display, color: p.ink, maxWidth: 1040, width: '100%', minWidth: 0, paddingBottom: 96, '--ob-hover': p.surf2 }}>
      <style>{CSS}</style>

      {/* Header */}
      <div>
        <h1 style={{ fontSize: 30, fontWeight: 500, letterSpacing: -1, color: p.ink, margin: 0 }}>Outreach</h1>
        <div style={{ fontSize: 13.5, color: p.ink3, marginTop: 6 }}>Sequences, replies and opt-outs.</div>
      </div>

      {/* Summary: three plain figures */}
      <div data-testid="outreach-summary" style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
        {summary.map(({ label, value }) => (
          <div key={label}>
            <div style={{ fontFamily: F.mono, fontSize: 26, fontWeight: 400, letterSpacing: -0.5, color: p.ink, lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 12.5, color: p.ink3, marginTop: 8 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="ob-seg" role="tablist" aria-label="Status" data-testid="outreach-status-filter"
          style={{ display: 'flex', borderBottom: `1px solid ${p.hair}`, gap: 22, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map(f => {
            const on = filter === f.key;
            return (
              <button key={f.key} role="tab" aria-selected={on} onClick={() => setFilter(f.key)}
                style={{ background: 'none', border: 'none', padding: '0 0 10px', marginBottom: -1, cursor: 'pointer', whiteSpace: 'nowrap',
                  fontFamily: F.display, fontSize: 14, fontWeight: on ? 500 : 400, color: on ? p.ink : p.ink3,
                  borderBottom: `2px solid ${on ? p.ink : 'transparent'}` }}>
                {f.label}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search prospects"
            aria-label="Search prospects"
            style={{ ...inputStyle(p), flex: 1, minWidth: 0 }}
          />
          <select value={segment} onChange={e => setSegment(e.target.value)} aria-label="Segment" data-testid="outreach-segment-filter"
            style={{ ...inputStyle(p), width: 'auto', flex: '0 0 auto', color: segment === 'all' ? p.ink3 : p.ink, cursor: 'pointer' }}>
            {SEGMENTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* List */}
      {visible.length === 0 ? (
        <div data-testid="outreach-empty" style={{ padding: '48px 0', fontSize: 14, color: p.ink3, borderTop: `1px solid ${p.hair}` }}>
          {emptyMessage(filter, { search, segment })}
        </div>
      ) : (
        <div data-testid="outreach-list" style={{ borderTop: `1px solid ${p.hair}` }}>
          {visible.map(r => {
            const label = statusLabel(r, today);
            const urgent = label === 'Overdue' || label === 'Due today';
            return (
              <div
                key={r.id}
                role="button"
                tabIndex={0}
                className="ob-row"
                data-testid="outreach-row"
                onClick={() => openDetail(r.id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(r.id); } }}
                style={{ padding: '16px 4px', borderBottom: `1px solid ${p.hair}`, cursor: 'pointer' }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: p.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                  <div style={{ fontSize: 12.5, color: p.ink3, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {contactLine(r) || segmentLabel(r)}
                  </div>
                </div>
                <div className="ob-status" style={{ fontSize: 12.5, fontWeight: urgent ? 500 : 400, color: urgent ? T.brickDeep : p.ink2, whiteSpace: 'nowrap' }}>
                  {label}
                </div>
                <div className="ob-next" style={{ minWidth: 0 }}>
                  <Meta p={p} items={nextStepParts(r).map((part, i, all) => ({
                    ...part,
                    tone: urgent && i === all.length - 1 && part.figure ? T.brickDeep : undefined,
                  }))} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent style={{ maxWidth: 720, width: 'calc(100vw - 24px)', maxHeight: '88vh', overflowY: 'auto', background: p.surf, color: p.ink }}>
          {pro && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: F.display, fontWeight: 500, letterSpacing: -0.4 }}>{pro.name}</DialogTitle>
                <DialogDescription style={{ color: p.ink3 }}>
                  {[segmentLabel(pro), pro.contact_person, pro.role].filter(Boolean).join(' · ')}
                </DialogDescription>
              </DialogHeader>

              {detailLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                  <BricksLoader variant="assemble" size={32} color={T.brick} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <Meta p={p} items={[
                    { text: statusLabel(pro, today), tone: p.ink },
                    { text: pro.channel === 'email' ? (pro.email || 'No email') : `@${pro.instagram || ''}`, figure: true },
                    pro.consent_basis && { text: pro.consent_basis.replace(/_/g, ' ') },
                    pro.sequence_halted && { text: pro.halt_reason ? `Halted: ${pro.halt_reason}` : 'Halted' },
                  ]} />

                  {pro.fit_note && (
                    <div style={{ fontSize: 13.5, color: p.ink2, lineHeight: 1.55 }}>{pro.fit_note}</div>
                  )}

                  {/* Touch timeline */}
                  <div>
                    <Label p={p}>Sequence</Label>
                    <div style={{ borderTop: `1px solid ${p.hair}` }}>
                      {(detail.touches || []).map(t => (
                        <div key={t.id} style={{ padding: '12px 0', borderBottom: `1px solid ${p.hair}` }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                            <Figure p={p}>{t.touch_no}/5</Figure>
                            <span style={{ fontSize: 13.5, fontWeight: 500, flex: '1 1 160px', minWidth: 0 }}>{t.subject || channelLabel(t)}</span>
                            <span style={{ fontSize: 12.5, color: p.ink3 }}>
                              {t.status === 'sent'
                                ? <>Sent <Figure p={p}>{fmtDateTime(t.sent_at)}</Figure></>
                                : <>{t.approval === 'approved' ? 'Approved' : 'Needs approval'}{t.due_date && <> · due <Figure p={p}>{shortDay(t.due_date)}</Figure></>}</>}
                            </span>
                            {t.status !== 'sent' && (
                              <Btn variant="ghost" disabled={busy || pro.sequence_halted} onClick={() => markSent(t.touch_no)} style={{ padding: '6px 12px', fontSize: 12 }}>
                                Mark sent
                              </Btn>
                            )}
                          </div>
                          {t.body && (
                            <div style={{ fontSize: 12.5, color: p.ink3, lineHeight: 1.55, marginTop: 8, whiteSpace: 'pre-wrap' }}>{t.body}</div>
                          )}
                          {t.message_ref && <div style={{ fontFamily: F.mono, fontSize: 10.5, color: p.ink4, marginTop: 6 }}>{t.message_ref}</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Replies */}
                  <div>
                    <Label p={p}>Replies and feedback</Label>
                    {(detail.replies || []).length === 0 ? (
                      <div style={{ fontSize: 13, color: p.ink3 }}>No replies logged yet.</div>
                    ) : (
                      <div style={{ borderTop: `1px solid ${p.hair}` }}>
                        {detail.replies.map(rep => (
                          <div key={rep.id} style={{ padding: '10px 0', borderBottom: `1px solid ${p.hair}` }}>
                            <div style={{ fontSize: 12.5, color: p.ink3, marginBottom: 6 }}>
                              <span style={{ color: rep.kind === 'unsubscribe' ? T.bad : p.ink2, textTransform: 'capitalize' }}>{rep.kind}</span>
                              {' · '}{channelLabel(rep)}{' · '}<Figure p={p}>{fmtDateTime(rep.received_at)}</Figure>
                            </div>
                            <div style={{ fontSize: 13.5, color: p.ink2, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{rep.body}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <textarea
                        value={replyBody}
                        onChange={e => setReplyBody(e.target.value)}
                        placeholder="Paste the reply or feedback. Logging a reply halts the sequence."
                        rows={3}
                        style={{ ...inputStyle(p), flex: '1 1 240px', resize: 'vertical' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <select value={replyKind} onChange={e => setReplyKind(e.target.value)} style={{ ...inputStyle(p), width: 130 }}>
                          <option value="reply">Reply</option>
                          <option value="feedback">Feedback</option>
                          <option value="unsubscribe">Unsubscribe</option>
                          <option value="note">Note</option>
                        </select>
                        <Btn disabled={busy} onClick={logReply} style={{ padding: '9px 14px', fontSize: 13 }}>Log</Btn>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter style={{ gap: 8, flexWrap: 'wrap' }}>
                <Btn variant="ghost" disabled={busy} onClick={toggleHalt} style={{ padding: '9px 14px', fontSize: 13 }}>
                  {pro.sequence_halted ? 'Resume sequence' : 'Halt sequence'}
                </Btn>
                <Btn variant="ghost" disabled={busy} onClick={suppress} style={{ padding: '9px 14px', fontSize: 13, color: T.bad }}>
                  Suppress contact
                </Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
